/**
 * MPACK rinkinio konstruktorius (build-a-box).
 *
 * Trys skoniai, po vieną `quantity-selector-component` kiekvienam — tas pats
 * Vessel'io skaitiklis, kurį tema jau naudoja produkto puslapyje ir krepšelyje,
 * ne naujai piešti +/− mygtukai. Jo pačio `max` atributą užstatome sandėlio
 * likučiu prisijungimo metu (`updateConstraints`), tad viršyti likutį fiziškai
 * negalima — nei paspaudus mygtuką, nei įrašius skaičių rankiniu būdu.
 *
 * Bendros sumos riba SĄMONINGAI nedaroma (brief 2026-09-19 patvirtinimas #2):
 * klientas gali laisvai koreguoti bet kurio skonio kiekį iki bet kokios sumos,
 * greitieji mygtukai (3/6/12/21) tik pasiūlo lygų pradinį pasiskirstymą.
 *
 * Kainos matematika gyvena `@theme/mpack-pack-split`, ta pati, kurią naudoja
 * ir skaičiuoklė (`mpack-calculator.js`). Krepšelio dalis — `mpack-cart-add.js`.
 */

import { addLines } from '@theme/mpack-cart-add';
import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';
import { parseTiers, percentForQuantity, roundedUnitPrice, splitEvenly } from '@theme/mpack-pack-split';

// Sėkmės žinutė dingsta pati, klaida — ne. Žr. mpack-calculator.js #message.
const MESSAGE_DURATION = 5000;

class MpackBoxBuilder extends HTMLElement {
  #abort = new AbortController();
  #messageTimer = null;
  #pulseTimer = null;
  #busy = false;

  connectedCallback() {
    const { signal } = this.#abort;

    this.presetGroup = this.querySelector('[data-box-preset]')?.closest('.mpack-box__presets') ?? null;
    this.presetButtons = Array.from(this.querySelectorAll('[data-box-preset]'));
    this.flavorsEl = this.querySelector('.mpack-box__flavors');
    this.selectors = Array.from(this.querySelectorAll('quantity-selector-component'));

    this.out = {
      total: this.querySelector('[data-box-total]'),
      packs: this.querySelector('[data-box-packs]'),
      was: this.querySelector('[data-box-was]'),
      unit: this.querySelector('[data-box-unit]'),
      hint: this.querySelector('[data-box-hint]'),
      note: this.querySelector('[data-box-note]'),
    };

    // Po vieną pastabą kiekvienam skoniui, ta pačia tvarka kaip `this.selectors`
    // (abu surenkami iš tų pačių `.mpack-box__flavor` blokų). Rodoma/slepiama
    // pagal TIKRĄ mygtuko disabled būseną — žr. #updateFlavorLocks.
    this.flavorMaxEls = Array.from(this.querySelectorAll('[data-flavor-max]'));

    this.addButton = this.querySelector('[data-box-add]');
    this.addLabel = this.querySelector('[data-box-add-label]');
    this.messageEl = this.querySelector('[data-box-msg-text]');
    this.messageBox = this.querySelector('[data-box-msg]');
    this.messageClose = this.querySelector('[data-box-msg-close]');

    this.strings = this.#readData();
    this.ids = (this.strings.ids ?? []).filter(Boolean);
    this.stock = (this.strings.stock ?? []).map((value) => Number(value) || 0);
    this.stockFree = Number(this.strings.stockFree) || 100000;

    this.unitBase = Number(this.dataset.unitBase) || 0;
    this.currency = this.dataset.currency || '';
    this.tiers = parseTiers(this.dataset.tiers);

    const formatTemplate = this.querySelector('[data-box-money-format]');
    this.moneyFormat = formatTemplate?.content?.textContent?.trim() || '{{amount}}';

    if (this.selectors.length !== this.ids.length) {
      console.warn('[mpack-box-builder] skaitiklių ir SKU sąrašo ilgiai nesutampa', this.selectors.length, this.ids.length);
    }

    // Kiekvieno skonio skaitiklis negali viršyti SAVO sandėlio likučio — ne
    // bendros sumos, o vieno konkretaus SKU. `updateConstraints` iškart
    // apkarpo pradinę reikšmę ir atnaujina mygtukų būseną.
    this.selectors.forEach((selector, index) => {
      const max = this.stock[index] ?? this.stockFree;
      selector.updateConstraints('0', String(max), '1');
    });
    this.#updateFlavorLocks();

    this.presetGroup?.addEventListener('click', this.#onPreset, { signal });
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQtyUpdate, { signal });
    this.addButton?.addEventListener('click', this.#onAdd, { signal });
    this.messageClose?.addEventListener('click', () => this.#message(''), { signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#abort.abort();
    if (this.#messageTimer) clearTimeout(this.#messageTimer);
    if (this.#pulseTimer) clearTimeout(this.#pulseTimer);
  }

  /* ---------------------------------------------------------------- data -- */

  #readData() {
    const script = this.querySelector('[data-box-data]');
    if (!script?.textContent) return { ids: [], stock: [] };
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[mpack-box-builder] bloga duomenų dalis', error);
      return { ids: [], stock: [] };
    }
  }

  /* ------------------------------------------------------------ skaičiai -- */

  get quantities() {
    return this.selectors.map((selector) => Number(selector.getValue()) || 0);
  }

  get state() {
    const packs = this.quantities.reduce((sum, value) => sum + value, 0);
    const percent = percentForQuantity(this.tiers, packs);
    const unit = roundedUnitPrice(this.unitBase, percent);
    const total = unit * packs;
    return { packs, percent, unit, total };
  }

  #money(cents) {
    return formatMoney(cents, this.moneyFormat, this.currency);
  }

  #render() {
    const s = this.state;

    if (this.out.packs) this.out.packs.textContent = String(s.packs);
    if (this.out.was) this.out.was.hidden = s.percent === 0;
    if (this.out.unit) this.out.unit.textContent = this.#money(s.unit);
    if (this.out.total) this.out.total.textContent = this.#money(s.total);
    if (this.out.hint) this.out.hint.hidden = s.packs > 0;

    if (this.addButton) this.addButton.disabled = this.#busy || s.packs === 0;

    // Kortelė lieka „paspausta" (aria-pressed + akcento fonas) TIK kol
    // bendra suma sutampa su jos pačios kiekiu — tai teisinga ir po greitojo
    // mygtuko paspaudimo, ir po bet kokio rankinio +/− pakeitimo, be
    // atskiros būsenos, kurią reikėtų sinchronizuoti su tikrove.
    this.presetButtons.forEach((button) => {
      const count = Number(button.dataset.boxPreset) || 0;
      button.setAttribute('aria-pressed', String(count === s.packs));
    });
  }

  /**
   * Trumpas skonių bloko fono blyksnis, kuris parodo, KAD greitasis mygtukas
   * ką tik perskirstė kiekius — pati skirstymo lentelė visada matoma, tad
   * čia tik akcentuojamas pokytis, ne slepiamas/rodomas naujas blokas.
   * Pertraukiama sekančio blyksnio, jei mygtukai spaudžiami paeiliui greitai.
   */
  #pulseFlavors() {
    if (!this.flavorsEl) return;
    if (this.#pulseTimer) clearTimeout(this.#pulseTimer);
    this.flavorsEl.classList.add('mpack-box__flavors--updated');
    this.#pulseTimer = setTimeout(() => {
      this.flavorsEl?.classList.remove('mpack-box__flavors--updated');
      this.#pulseTimer = null;
    }, 700);
  }

  /**
   * Rodo arba paslepia pastabą, kad greitasis mygtukas kai kuriems skoniams
   * turėjo apkarpyti pasiūlymą dėl likučio. Rankinis koregavimas ją nuima:
   * tai buvo paaiškinimas TAM konkrečiam pasiūlymui, ne nuolatinis įspėjimas.
   */
  #setNote(visible) {
    if (this.out.note) this.out.note.hidden = !visible;
  }

  /**
   * Brief p.5: pasiekus skonio likutį, mygtukas turi UŽSIRAKINTI SU ĮSPĖJIMU
   * prieš bandant spausti toliau, ne tyliai apkarpyti po fakto. Pats mygtukas
   * jau užsirakina (component-quantity-selector.js updateButtonStates), bet
   * be teksto — čia paskaitoma jo TIKRA `disabled` būsena kiekvienam skoniui
   * atskirai ir rodoma/slepiama atitinkama pastaba. Kviečiama po kiekvieno
   * pokyčio (prisijungimo, greitojo mygtuko, rankinio +/−), tad įspėjimas
   * atsiranda TUO PAČIU metu, kai mygtukas užsirakina, ne vėliau.
   */
  #updateFlavorLocks() {
    // `quantity-selector:update` yra išsiunčiamas PRIEŠ pačiam skaitikliui
    // atnaujinant mygtukų `disabled` būseną (component-quantity-selector.js
    // `increaseQuantity`: `onQuantityChange()` → dispatch, TADA
    // `updateButtonStates()`). Skaitant `disabled` sinchroniškai čia gaunama
    // sena reikšmė — būtent tą kritinę akimirką, kai mygtukas užsirakina.
    // Microtask'as paleidžiamas iškart po sinchroninio kodo, bet prieš
    // piešimą, tad `disabled` jau būna teisingas.
    queueMicrotask(() => {
      this.selectors.forEach((selector, index) => {
        const plusButton = selector.querySelector('.quantity-plus');
        const el = this.flavorMaxEls[index];
        if (el) el.hidden = !plusButton?.disabled;
      });
    });
  }

  /* ------------------------------------------------------------- įvykiai -- */

  #onPreset = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-box-preset]') : null;
    if (!button || !this.presetGroup?.contains(button)) return;

    const requested = Number(button.dataset.boxPreset) || 0;
    const shares = splitEvenly(requested, this.selectors.length);

    let clamped = false;
    this.selectors.forEach((selector, index) => {
      const share = shares[index] ?? 0;
      const capped = Math.min(share, this.stock[index] ?? this.stockFree);
      if (capped < share) clamped = true;
      selector.setValue(String(capped));
      selector.updateButtonStates();
    });

    this.#setNote(clamped);
    this.#updateFlavorLocks();
    this.#pulseFlavors();
    this.#render();
  };

  #onQtyUpdate = () => {
    this.#setNote(false);
    this.#updateFlavorLocks();
    this.#render();
  };

  /* ------------------------------------------------------------ krepšelis -- */

  #split() {
    const quantities = this.quantities;
    return this.ids
      .map((id, index) => ({ id: Number(id), quantity: quantities[index] ?? 0 }))
      .filter((line) => line.quantity > 0);
  }

  #message(text, isError = false) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text ?? '';
    this.messageBox?.classList.toggle('mpack-box__msg--error', Boolean(text) && isError);
    if (this.messageClose) this.messageClose.hidden = !(text && isError);

    if (this.#messageTimer) clearTimeout(this.#messageTimer);
    this.#messageTimer = null;
    if (text && !isError) {
      this.#messageTimer = setTimeout(() => this.#message(''), MESSAGE_DURATION);
    }
  }

  #setBusy(busy) {
    this.#busy = busy;
    if (this.addLabel) this.addLabel.textContent = busy ? this.strings.adding : this.strings.add;
    this.#render();
  }

  #onAdd = async () => {
    if (this.#busy) return;

    const s = this.state;
    if (s.packs === 0) return;

    const lines = this.#split();
    if (lines.length === 0) return;

    this.#setBusy(true);
    this.#message('');

    try {
      const result = await addLines({ host: this, lines, source: 'mpack-box-builder' });

      if (result.ok) {
        this.#message(this.strings.added);
      } else {
        this.#message(result.message || this.strings.error, true);
      }
    } finally {
      this.#setBusy(false);
    }
  };
}

if (!customElements.get('mpack-box-builder')) {
  customElements.define('mpack-box-builder', MpackBoxBuilder);
}
